import { useState, useEffect, useRef } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { getStoredDisplayName, saveDisplayName } from '../displayName';
import { defaultAvatar, getCategoriesForLobby, getCategoryMeta, isOffensive, tierFor } from '@brainfart/shared';
import { SocialLinks } from './SocialLinks';
import { FeedbackLink } from './FeedbackLink';
import { InstallButton, InstallSheet } from './InstallSheet';
import { RestoreSheet } from './RestoreSheet';
import { BackButton } from './BackButton';
import { bestCategory, getProfile } from '../profileStore';
import { saveIdentity, useIdentity } from '../identity';
import { STARTER_TOPICS } from '../starterTopics';
import { ProfileChip } from './ProfileSheet';
import { Sheet } from './Sheet';
import { cachedRatings } from '../ratingsCache';
import { track } from '../telemetry';
import { fetchInbox, type InboxItem } from '../duelInbox';
import { LaunchedTopicBanner } from './LaunchedTopicBanner';
import { QuestsCard } from './QuestsCard';

const CATEGORY_COUNT = getCategoriesForLobby().length;

interface JoinScreenProps {
  onCreate: (name: string, settings?: any) => void;
  onJoin: (name: string, code: string) => void;
  initialRoom?: string;
  onPlaySolo?: (name: string) => void;
  onPlayDuel?: (category?: string) => void;
  onPlayDaily?: () => void;
  onViewStats?: () => void;
}

type View = 'choose' | 'host' | 'join' | 'solo';

export function JoinScreen({ onCreate, onJoin, initialRoom = '', onPlaySolo, onPlayDaily, onPlayDuel, onViewStats }: JoinScreenProps) {
  const linkedJoin = Boolean(initialRoom);
  const [view, setView] = useState<View>(linkedJoin ? 'join' : 'choose');
  const [name, setName] = useState(getStoredDisplayName);
  const [code, setCode] = useState(initialRoom);
  const [error, setError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const duelNews = useDuelNews(Boolean(onPlayDuel));
  const me = useIdentity();
  const streak = getProfile().streak.count;
  const dailySubtitle = streak > 1 ? `🔥 ${streak}-day streak · 8 questions` : '8 questions · same for everyone today';

  useEffect(() => {
    if (view !== 'choose') nameRef.current?.focus();
  }, [view]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Enter your name, genius');
      return;
    }
    if (isOffensive(name)) {
      setError("Let's keep it friendly. Pick another name");
      return;
    }
    saveDisplayName(name);
    if (view === 'host') {
      onCreate(name.trim());
    } else if (view === 'solo') {
      onPlaySolo?.(name.trim());
    } else {
      if (!code.trim()) {
        setError('Enter the room code');
        return;
      }
      onJoin(name.trim(), code.trim().toUpperCase());
    }
  };

  return (
    <m.div
      className="join-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ minHeight: '100vh', padding: '2.5rem 0 3rem', position: 'relative', overflow: 'hidden' }}
    >
      {/* Ambient glow behind the hero — makes the landing screen feel like
          a destination, not just a form. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-280px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '700px',
          maxWidth: '160vw',
          background: 'radial-gradient(ellipse at center, rgba(255,201,60,0.14) 0%, rgba(57,255,136,0.06) 45%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', padding: '0 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 640, margin: '0 auto 0.5rem' }}>
          {onViewStats ? (
            <button className="icon-btn" onClick={onViewStats} aria-label="My stats" title="My stats">
              <span aria-hidden>📊</span>
            </button>
          ) : <span />}
          <ProfileChip />
        </div>
        {/* Logo/Title — click to return here from any sub-view */}
        <m.button
          onClick={() => setView('choose')}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            display: 'block',
            width: '100%',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '1.25rem',
            textAlign: 'center',
          }}
        >
          <h1 style={{
            fontSize: 'clamp(2.4rem, 8vw, 3.6rem)',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
            letterSpacing: '-0.02em',
          }}>
            BrainFart<span style={{ color: '#FFC93C', WebkitTextFillColor: '#FFC93C' }}>.fun</span>
          </h1>
          <p style={{ color: '#9a97b3', fontSize: '1.05rem' }}>Fast trivia quizzes. Beat the clock.</p>
        </m.button>

        <AnimatePresence mode="wait">
          {view === 'choose' && (
            <m.div
              key="choose"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                {onPlayDuel && <PlayHero news={duelNews} onPlayDuel={onPlayDuel} />}
                {onPlayDuel && <LaunchedTopicBanner onPlay={onPlayDuel} />}
                {onPlayDaily && (
                  <ActionCard
                    compact
                    icon="🗓️"
                    title="Daily Quiz"
                    subtitle={dailySubtitle}
                    accentFrom="#7C5CFF"
                    accentTo="#5B3FD9"
                    textColor="#fff"
                    onClick={onPlayDaily}
                  />
                )}
                {onPlayDuel && <QuestsCard />}
                <FriendsCard
                  onHost={() => (me.name ? onCreate(me.name) : setView('host'))}
                  onJoin={() => setView('join')}
                />
                {onPlaySolo && (
                  <button
                    onClick={() => onPlaySolo(me.name || startingName())}
                    style={{ alignSelf: 'center', color: '#bbb', fontSize: '0.95rem', fontWeight: 700, minHeight: 44, padding: '0 1rem' }}
                  >
                    🎯 Practice solo ›
                  </button>
                )}
              </div>

              <div style={{ maxWidth: 640, margin: '1.25rem auto 0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                <RestoreSheet />
                <InstallButton where="home_button" variant="link" />
                <span aria-hidden style={{ color: '#444' }}>·</span>
                <FeedbackLink from="home" variant="link" />
                <span aria-hidden style={{ color: '#444' }}>·</span>
                <a href="/privacy" className="text-link">Privacy</a>
                {hasPlayedBefore() && <InstallSheet where="home" delayMs={2500} />}
              </div>
              <SocialLinks />
            </m.div>
          )}

          {view !== 'choose' && (
            <m.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} style={{ maxWidth: 480, margin: '0 auto' }}>
              {!linkedJoin && (
                <BackButton onClick={() => setView('choose')} label="Back" style={{ marginBottom: '1rem' }} />
              )}

              {linkedJoin && (
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{
                    textAlign: 'center',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'rgba(255,201,60,0.08)',
                    border: '1px solid rgba(255,201,60,0.25)',
                    borderRadius: '14px',
                    color: '#FFC93C',
                    fontWeight: 600,
                  }}
                >
                  You're joining <span style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}>{initialRoom}</span> — what should we call you?
                </m.div>
              )}

              <form
                onSubmit={handleSubmit}
                style={{ background: '#1B1830', border: '1px solid #2E2B45', borderRadius: '20px', padding: '2rem' }}
              >
                {error && (
                  <m.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ color: '#FF4D6A', marginBottom: '1rem', padding: '0.75rem', background: 'rgba(255,77,106,0.1)', borderRadius: '8px', fontSize: '0.9rem' }}
                  >
                    {error}
                  </m.div>
                )}

                {view === 'solo' && (
                  <p style={{ color: '#888', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                    Just your name — pick your categories next.
                  </p>
                )}

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#ccc' }}>
                    Your Name
                  </label>
                  <input
                    ref={nameRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 20))}
                    maxLength={20}
                    placeholder="Enter your name..."
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      fontSize: '1.1rem',
                      borderRadius: '12px',
                      border: '1px solid #2E2B45',
                      background: '#100E22',
                      color: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#FFC93C'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#2E2B45'}
                  />
                </div>

                {view === 'join' && !linkedJoin && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#ccc' }}>
                      Room Code
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      maxLength={6}
                      placeholder="ABC123"
                      style={{
                        width: '100%',
                        padding: '1rem 1.25rem',
                        fontSize: '1.5rem',
                        letterSpacing: '0.3em',
                        textTransform: 'uppercase',
                        borderRadius: '12px',
                        border: '1px solid #2E2B45',
                        background: '#100E22',
                        color: '#FFC93C',
                        outline: 'none',
                        textAlign: 'center',
                      }}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  className={view === 'solo' ? 'btn' : 'btn btn-primary'}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    ...(view === 'solo' ? { background: 'linear-gradient(135deg, #39FF88, #22CC66)', color: '#000', fontWeight: 700, borderRadius: '16px' } : {}),
                  }}
                >
                  {view === 'host' ? 'Start a room' : view === 'solo' ? '🎯 Start practising' : 'Join the room'}
                </button>
              </form>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </m.div>
  );
}

// First-time visitors should see the game, not an install pitch.
function hasPlayedBefore(): boolean {
  const { stats, streak, categoryXP } = getProfile();
  return stats.soloGames + stats.dailyGames > 0 || streak.count > 0 || Object.keys(categoryXP).length > 0;
}

// Unseen duel results for the homepage card: "Neha beat your score!"
function useDuelNews(enabled: boolean): { unseen: number; line: string } | null {
  const [news, setNews] = useState<{ unseen: number; line: string } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchInbox()
      .then(({ items, unseen }) => {
        const latest = items.find(i => !i.seen);
        if (!cancelled && unseen > 0 && latest) setNews({ unseen, line: newsLine(latest, unseen) });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);
  return news;
}

function newsLine(item: InboxItem, unseen: number): string {
  const who = item.fromName || 'Someone';
  const more = unseen > 1 ? ` (+${unseen - 1} more)` : '';
  if (item.kind === 'rematch') return `${who} challenged you back. Your move!${more}`;
  if (item.theirScore > (item.yourScore ?? 0)) return `${who} beat your score. Get revenge!${more}`;
  return `${who} took on your score and lost.${more}`;
}

function ActionCard({ icon, title, subtitle, accentFrom, accentTo, textColor, border, badge, compact, onClick }: {
  compact?: boolean;
  icon: string;
  title: string;
  subtitle: string;
  accentFrom: string;
  accentTo: string;
  textColor: string;
  border?: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <m.button
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '1rem' : '1.5rem',
        width: '100%',
        padding: compact ? '1.1rem 1.25rem' : '1.75rem 2rem',
        borderRadius: '22px',
        border: border ? `1px solid ${border}` : 'none',
        background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})`,
        color: textColor,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      {badge ? (
        <m.span
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          aria-label={`${badge} new`}
          style={{
            position: 'absolute', top: -8, right: -6, minWidth: 26, height: 26, padding: '0 7px', borderRadius: 13,
            background: '#FFC93C', color: '#1A1330', fontWeight: 900, fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {badge}
        </m.span>
      ) : null}
      <span style={{ fontSize: compact ? '2rem' : '2.5rem', lineHeight: 1 }}>{icon}</span>
      <span>
        <span style={{ display: 'block', fontSize: compact ? '1.25rem' : '1.4rem', fontWeight: 700 }}>{title}</span>
        <span style={{ display: 'block', fontSize: '0.95rem', opacity: 0.8, fontWeight: 500 }}>{subtitle}</span>
      </span>
    </m.button>
  );
}

// The one big button: a quick duel in your best category. A first-timer picks
// a player card and a topic first, then goes straight into their first duel.
function PlayHero({ news, onPlayDuel }: { news: { unseen: number; line: string } | null; onPlayDuel: (category?: string) => void }) {
  const me = useIdentity();
  const [step, setStep] = useState<'none' | 'topic'>('none');
  const best = bestCategory();
  const meta = best ? getCategoryMeta(best) : null;
  const rating = best ? cachedRatings()[best] : undefined;

  const play = () => {
    track({ type: 'play_clicked', payload: { hasProfile: Boolean(me.name), category: best } });
    // First time (or no favourite yet): one sheet, one tap: name (optional) + topic.
    if (!me.name || !best) return setStep('topic');
    onPlayDuel(best);
  };

  const subtitle = !me.name
    ? 'Your first quiz takes a minute'
    : meta
      ? `Quick quiz · ${meta.icon} ${meta.title}${rating ? ` · ${tierFor(rating.rating).icon} ${rating.rating.toLocaleString()}` : ''}`
      : 'Quick quiz · pick a topic';

  return (
    <div>
      <m.button
        onClick={play}
        whileTap={{ scale: 0.97 }}
        style={{
          position: 'relative', width: '100%', padding: '1.35rem 1.25rem', borderRadius: '24px', border: '3px solid #1A1330',
          background: 'linear-gradient(135deg, #FF4D6A, #C9184A)', boxShadow: '5px 5px 0 #1A1330', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '1rem', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '2.6rem', lineHeight: 1 }}>▶️</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '2rem', fontWeight: 900, letterSpacing: '0.02em', lineHeight: 1.05 }}>PLAY</span>
          <span style={{ display: 'block', fontSize: '0.9rem', opacity: 0.9, fontWeight: 600 }}>{subtitle}</span>
        </span>
        {news?.unseen ? (
          <span aria-label={`${news.unseen} new`} style={{
            position: 'absolute', top: -8, right: -6, minWidth: 26, height: 26, padding: '0 7px', borderRadius: 13, background: '#FFC93C',
            color: '#1A1330', fontWeight: 900, fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{news.unseen}</span>
        ) : null}
      </m.button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', padding: '0 0.25rem' }}>
        {news ? (
          <button onClick={() => onPlayDuel()} style={{ color: '#FFC93C', fontSize: '0.85rem', fontWeight: 700, textAlign: 'left', minHeight: 40 }}>
            💬 {news.line}
          </button>
        ) : <span />}
        <button onClick={() => onPlayDuel()} style={{ color: '#aaa', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0, minHeight: 40 }}>
          All topics ›
        </button>
      </div>

      <TopicSheet
        open={step === 'topic'}
        askName={!me.name}
        onClose={() => setStep('none')}
        onPick={category => {
          track({ type: 'onboarding_topic_picked', payload: { category } });
          setStep('none');
          onPlayDuel(category);
        }}
        onMore={() => { setStep('none'); onPlayDuel(); }}
      />
    </div>
  );
}

// Someone who hasn't named themselves gets a friendly "Player123" (editable any
// time from the chip), so practice never stops to ask.
function startingName(): string {
  const name = `Player${100 + Math.floor(Math.random() * 900)}`;
  saveIdentity(name, defaultAvatar(name));
  return name;
}

// First run in one step: an optional name, then tap a topic and the quiz starts.
// No name typed gets a friendly "Player123" (editable any time from the chip).
function TopicSheet({ open, askName, onClose, onPick, onMore }: {
  open: boolean;
  askName: boolean;
  onClose: () => void;
  onPick: (c: string) => void;
  onMore: () => void;
}) {
  const [name, setName] = useState('');
  const blocked = isOffensive(name);

  const pick = (key: string) => {
    if (askName) {
      if (blocked) return;
      if (!name.trim()) startingName(); else saveIdentity(name.trim(), defaultAvatar(name.trim()));
    }
    onPick(key);
  };

  return (
    <Sheet open={open} onClose={onClose} label="Pick a topic">
      <div style={{ textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#FFC93C', marginBottom: '0.75rem' }}>Pick a topic to start</h2>
        {askName && (
          <>
            <input
              value={name}
              onChange={e => setName(e.target.value.slice(0, 20))}
              placeholder="Your name (optional)"
              aria-label="Your name"
              maxLength={20}
              autoComplete="nickname"
              enterKeyHint="done"
              style={{
                width: '100%', padding: '0.8rem 1rem', borderRadius: 12, border: '1px solid #2E2B45', background: '#100E22',
                color: '#fff', fontSize: '1rem', outline: 'none', textAlign: 'center', marginBottom: blocked ? '0.35rem' : '0.9rem',
              }}
            />
            {blocked && <p role="alert" style={{ color: '#FF6B6B', fontSize: '0.8rem', marginBottom: '0.75rem' }}>That name won't be shown to other players. Try another?</p>}
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.5rem' }}>
          {STARTER_TOPICS.map(key => {
            const meta = getCategoryMeta(key);
            if (!meta) return null;
            return (
              <button
                key={key}
                onClick={() => pick(key)}
                disabled={blocked}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.9rem 0.75rem', minHeight: 56, borderRadius: '14px', cursor: 'pointer',
                  border: `1px solid ${meta.color}66`, background: `linear-gradient(135deg, ${meta.color}33, ${meta.color}10)`, color: '#fff', fontWeight: 700, textAlign: 'left',
                  opacity: blocked ? 0.5 : 1,
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>{meta.icon}</span>
                <span style={{ fontSize: '0.9rem' }}>{meta.title}</span>
              </button>
            );
          })}
        </div>
        <button className="btn btn-ghost" onClick={onMore} style={{ fontSize: '0.9rem' }}>More topics ({CATEGORY_COUNT})</button>
      </div>
    </Sheet>
  );
}

function FriendsCard({ onHost, onJoin }: { onHost: () => void; onJoin: () => void }) {
  return (
    <div style={{ padding: '1.1rem 1.25rem', borderRadius: '22px', background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', color: '#000' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '2rem', lineHeight: 1 }}>🎉</span>
        <span>
          <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800 }}>Quiz with friends</span>
          <span style={{ display: 'block', fontSize: '0.9rem', opacity: 0.8, fontWeight: 500 }}>A live trivia room for up to 8.</span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: '0.6rem' }}>
        <button onClick={onHost} style={{ flex: 1, minHeight: 48, borderRadius: '14px', background: '#1A1330', color: '#FFC93C', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}>
          Host a room
        </button>
        <button onClick={onJoin} style={{ flex: 1, minHeight: 48, borderRadius: '14px', background: 'rgba(26,19,48,0.12)', border: '2px solid #1A1330', color: '#1A1330', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}>
          Join with code
        </button>
      </div>
    </div>
  );
}
